export const ZONE_ORDER = ["exclusion", "activation", "detection", "beacon", "remote", "beyond"]

/** Diagram colors from `/tmp/zones_*.png`: red/pink, green, orange, blue, stone/brown. */
export const ZONE_COLOR = {
  exclusion: 0xf43f5e,
  activation: 0x4ade80,
  detection: 0xfb923c,
  beacon: 0x60a5fa,
  remote: 0xb8a48a,
}

export function zoneCss(zone) {
  const n = ZONE_COLOR[zone]
  if (typeof n !== "number") return "#94a3b8"
  return `#${n.toString(16).padStart(6, "0")}`
}

export const EARTH_M = 6_371_000
export const HORIZON_EPS = (1.5 * Math.PI) / 180
export const SKY_RIG_M = 120
export const SEEK_HIDE_M = 20_000
export const G_MIN_M = 4
export const G_SIGMA = 0.8
export const T_OUT_MS = 1500
export const T_IN_MS = 400
export const VIEWPORT_MIN = 0.2
export const VIEWPORT_MAX = 0.5
export const VIEWPORT_DEFAULT = 0.36
export const CONTAIN_MIN = 0.7
export const CONTAIN_MAX = 0.9
export const CONTAIN_DEFAULT = 0.9
export const DEFAULT_SIZE_ACTIVATION = 1.5
export const DEFAULT_SIZE_DETECTION = 8

export function pinFlag(value) {
  return value === true || value === 1 || value === "true" || value === "1"
}

export function radiiFromPin(pin) {
  return {
    exclusion: Number(pin.exclusion_radius_m) || 0,
    activation: Number(pin.activation_radius_m) || 0,
    detection: Number(pin.detection_radius_m) || 0,
    beacon: Number(pin.beacon_radius_m) || 0,
    remote: Number(pin.remote_radius_m) || 0,
    remoteFinite: pinFlag(pin.remote_finite),
  }
}

export function candidateZone(d, r) {
  if (!Number.isFinite(d) || d < 0 || !r) return "beyond"
  if (d < r.exclusion) return "exclusion"
  if (d <= r.activation) return "activation"
  if (d <= r.detection) return "detection"
  if (d <= r.beacon) return "beacon"
  if (!r.remoteFinite) return "remote"
  if (Number.isFinite(r.remote) && d <= r.remote) return "remote"
  return "beyond"
}

export function bandThickness(zone, r) {
  switch (zone) {
    case "exclusion":
      return Math.max(0, r.exclusion)
    case "activation":
      return Math.max(0, r.activation - r.exclusion)
    case "detection":
      return Math.max(0, r.detection - r.activation)
    case "beacon":
      return Math.max(0, r.beacon - r.detection)
    case "remote":
      return r.remoteFinite ? Math.max(0, r.remote - r.beacon) : Infinity
    case "beyond":
      return Infinity
    default:
      return 0
  }
}

export function guardM(sigma) {
  const s = Number.isFinite(sigma) ? sigma : 12
  return Math.max(G_MIN_M, G_SIGMA * s)
}

export function zoneIndex(zone) {
  const i = ZONE_ORDER.indexOf(zone)
  return i < 0 ? ZONE_ORDER.length - 1 : i
}

/**
 * The pin whose exclusion cylinder the fan is standing in, nearest first.
 *
 * Exclusion is the ground under the fan, not one pin's artwork: a campaign
 * draws one hologram, so standing on any of its pins hides the show and the
 * card must name the pin at the fan's feet. Entries are `{zone, rawHoriz}`.
 */
export function exclusionOwner(geos) {
  if (!Array.isArray(geos)) return null
  let best = null
  for (const geo of geos) {
    if (!geo || geo.zone !== "exclusion") continue
    if (!best || geo.rawHoriz < best.rawHoriz) best = geo
  }
  return best
}

export function isOutward(from, to) {
  if (to === "exclusion") return true
  if (from === "exclusion") return false
  return zoneIndex(to) > zoneIndex(from)
}

export function pastBoundary(d, from, to, r, g) {
  if (to === "exclusion") return d < r.exclusion - g
  if (from === "exclusion") return d > r.exclusion + g
  const cut = outerOfInner(from, to, r)
  if (cut == null) return true
  if (isOutward(from, to)) return d > cut + g
  return d < cut - g
}

function outerOfInner(from, to, r) {
  const inner = zoneIndex(from) < zoneIndex(to) ? from : to
  switch (inner) {
    case "exclusion":
      return r.exclusion
    case "activation":
      return r.activation
    case "detection":
      return r.detection
    case "beacon":
      return r.beacon
    case "remote":
      return r.remoteFinite ? r.remote : null
    default:
      return null
  }
}

export function createStickyZone() {
  let zone = null
  let pending = null
  let pendingSince = 0

  return {
    reset() {
      zone = null
      pending = null
      pendingSince = 0
    },
    peek() {
      return zone
    },
    update(d, sigma, r, now) {
      const g = guardM(sigma)
      let candidate = candidateZone(d, r)
      if (zone == null) {
        if (candidate === "exclusion" && d + g >= r.exclusion) candidate = "activation"
        zone = candidate
        pending = null
        return zone
      }
      if (candidate === zone) {
        pending = null
        return zone
      }
      const destThick = bandThickness(candidate, r)
      if (Number.isFinite(destThick) && Number.isFinite(sigma) && sigma > destThick) {
        pending = null
        return zone
      }
      if (!pastBoundary(d, zone, candidate, r, g)) {
        pending = null
        return zone
      }
      const wait = isOutward(zone, candidate) ? T_OUT_MS : T_IN_MS
      if (pending !== candidate) {
        pending = candidate
        pendingSince = now
        return zone
      }
      if (now - pendingSince >= wait) {
        zone = candidate
        pending = null
      }
      return zone
    },
  }
}

export function haversineM(lat1, lng1, lat2, lng2) {
  const r = EARTH_M
  const p1 = (lat1 * Math.PI) / 180
  const p2 = (lat2 * Math.PI) / 180
  const dp = ((lat2 - lat1) * Math.PI) / 180
  const dl = ((lng2 - lng1) * Math.PI) / 180
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)))
}

export function initialBearingDeg(lat1, lng1, lat2, lng2) {
  const p1 = (lat1 * Math.PI) / 180
  const p2 = (lat2 * Math.PI) / 180
  const dl = ((lng2 - lng1) * Math.PI) / 180
  const y = Math.sin(dl) * Math.cos(p2)
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl)
  return (Math.atan2(y, x) * 180) / Math.PI
}

export function aglForZone(zone, pin) {
  if (zone === "detection" || zone === "beacon") return Number(pin.altitude_agl_detection) || 0
  if (zone === "remote") return Number(pin.altitude_agl_remote) || 0
  return Number(pin.altitude_agl_activation) || 0
}

export function facingForZone(zone, pinFacing) {
  if (zone === "beacon" || zone === "remote") return "billboard"
  return pinFacing === "world" ? "world" : "billboard"
}

export function lingerSeconds(zone, pin) {
  const n = Number(pin?.[`linger_seconds_${zone}`])
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function lingerAllowed(zone, pin) {
  return lingerSeconds(zone, pin) > 0
}

export function tapToPlay(zone, pin) {
  if (!overlayVisible(zone)) return false
  const value = pin?.[`tap_to_play_${zone}`]
  if (value === undefined || value === null) return true
  return pinFlag(value)
}

export function overlayVisible(zone) {
  return zone === "activation" || zone === "detection" || zone === "beacon" || zone === "remote"
}

export function tapHintZone(zone, pin) {
  return overlayVisible(zone) && tapToPlay(zone, pin) && !lingerAllowed(zone, pin)
}

export function containFill(pin) {
  const n = Number(pin?.handoff_contain_fraction)
  if (!Number.isFinite(n)) return CONTAIN_DEFAULT
  return Math.min(CONTAIN_MAX, Math.max(CONTAIN_MIN, n))
}

export function seekAllowed(zone, d) {
  if (!overlayVisible(zone)) return false
  if (zone === "remote" && d > SEEK_HIDE_M) return false
  return true
}

export function sizeModeForZone(zone, kind) {
  if (zone === "beacon" || zone === "remote") return "longest"
  return kind === "glb" ? "diameter" : "longest"
}

export function sizeMForViewportFraction({fraction, canvasW, canvasH, fovH, slantM, aspect}) {
  const w = Math.max(canvasW, 1)
  const h = Math.max(canvasH, 1)
  const frac = Math.min(VIEWPORT_MAX, Math.max(VIEWPORT_MIN, Number(fraction) || VIEWPORT_DEFAULT))
  const longestPx = frac * Math.min(w, h)
  const s = Math.max(slantM, 0.5)
  const fovV = 2 * Math.atan(Math.tan(fovH / 2) / (w / h))
  const ratio = aspect > 0 ? aspect : 1
  if (ratio >= 1) {
    return (longestPx / w) * (2 * s * Math.tan(fovH / 2))
  }
  return (longestPx / h) * (2 * s * Math.tan(fovV / 2))
}

export function sizeMForZone(zone, pin, viewport) {
  if (zone === "activation") {
    return Number(pin.apparent_size_activation) > 0
      ? Number(pin.apparent_size_activation)
      : DEFAULT_SIZE_ACTIVATION
  }
  if (zone === "detection") {
    return Number(pin.apparent_size_detection) > 0
      ? Number(pin.apparent_size_detection)
      : DEFAULT_SIZE_DETECTION
  }
  const fraction =
    zone === "remote" ? Number(pin.remote_viewport_fraction) : Number(pin.beacon_viewport_fraction)
  return sizeMForViewportFraction({...viewport, fraction})
}

export function skyRigEnu(bearingDeg, agl, eye, distM) {
  const elev = Math.max(Math.atan2(agl - eye, Math.max(distM, 1)), HORIZON_EPS)
  const bearing = (bearingDeg * Math.PI) / 180
  const s = SKY_RIG_M
  return {
    east: Math.sin(bearing) * s * Math.cos(elev),
    north: Math.cos(bearing) * s * Math.cos(elev),
    up: s * Math.sin(elev),
  }
}
